import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api, type ApiResponse } from "@/api/client";
import { ORDER_STATUS_LABELS, type OrderStatus, type User } from "@ckgo2/shared/types";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import { ORDER_STATUS_CONFIG } from "@/lib/statusConfig";
import FacetedListLayout from "@/components/common/FacetedListLayout";
import FacetPanel, { type FacetBucket } from "@/components/common/FacetPanel";
import { ShoppingCart, Eye, Phone, Mail, Calendar, LayoutGrid, List, Plus } from "lucide-react";

interface OrderRow {
  id: string;
  orderNumber: string;
  status: OrderStatus;
  totalPrice: number;
  currency: string;
  requestedDate?: string | null;
  assignedTo?: string | null;
  customer?: { firstName?: string; lastName?: string; email?: string; phone?: string } | null;
  trip?: { title?: string } | null;
  tripDate?: { startDate?: string } | null;
}

interface FacetedOrders {
  data: OrderRow[];
  total: number;
  page: number;
  limit: number;
  facets: Record<string, FacetBucket[]>;
  summary: { totalValue: number };
  success: true;
}

export default function OrdersPage() {
  const [searchParams] = useSearchParams();
  const qs = searchParams.toString();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["orders", qs],
    queryFn: () => api.get<FacetedOrders>(`/api/admin/orders?${qs}`),
  });

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const userName = (id: string) => usersData?.data.find((u) => u.id === id)?.name ?? id;

  const orders = data?.data ?? [];
  const facets = data?.facets ?? {};
  const total = data?.total ?? 0;
  const limit = data?.limit ?? 25;

  // Stat cards from the status facet buckets (true within the current search/agent filter).
  const statusCount = (s: string) => facets.status?.find((b) => b.value === s)?.count ?? 0;
  const inquiries = statusCount("inquiry");
  const awaiting = statusCount("confirmed") + statusCount("deposit_paid");
  const fullyPaid = statusCount("fully_paid");
  const totalValue = data?.summary?.totalValue ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Orders</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Manage inquiries and orders</p>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/orders/new" className="btn-primary">
            <Plus className="h-5 w-5" />
            New order
          </Link>
          <Link to="/orders" className="btn-ghost">
            <List className="h-5 w-5" />
            Table
          </Link>
          <Link to="/orders/kanban" className="btn-ghost">
            <LayoutGrid className="h-5 w-5" />
            Kanban
          </Link>
        </div>
      </div>

      {/* Stat cards (reflect the current filter context) */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">New inquiries</p>
          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{inquiries}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Awaiting payment</p>
          <p className="text-warning-600 mt-1 text-2xl font-bold">{awaiting}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Fully paid</p>
          <p className="text-success-600 mt-1 text-2xl font-bold">{fullyPaid}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total value</p>
          <p className="text-primary-600 mt-1 text-2xl font-bold">{formatPrice(totalValue)}</p>
        </div>
      </div>

      <FacetedListLayout
        searchPlaceholder="Search by order #, customer, email, or trip…"
        total={total}
        limit={limit}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={
          <>
            <FacetPanel
              title="Status"
              paramKey="status"
              buckets={facets.status ?? []}
              labelFor={(v) => ORDER_STATUS_LABELS[v as OrderStatus] ?? v}
            />
            <FacetPanel
              title="Agent"
              paramKey="assignedTo"
              buckets={facets.assignedTo ?? []}
              labelFor={userName}
              nullLabel="Unassigned"
            />
          </>
        }
      >
        <div className="card overflow-hidden p-0">
          {orders.length === 0 ? (
            <div className="flex h-64 flex-col items-center justify-center text-gray-500">
              <ShoppingCart className="mb-2 h-12 w-12 text-gray-300" />
              <p>No orders found</p>
            </div>
          ) : (
            <table className="w-full">
              <thead className="border-b border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50">
                <tr>
                  {["Order", "Customer", "Trip", "Date", "Price", "Status", ""].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {orders.map((order) => {
                  const cfg = ORDER_STATUS_CONFIG[order.status] || ORDER_STATUS_CONFIG.inquiry;
                  return (
                    <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <Link
                          to={`/orders/${order.id}`}
                          className="text-primary-600 dark:text-primary-400 font-mono text-sm font-medium hover:underline"
                        >
                          {order.orderNumber}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-white">
                          {order.customer?.firstName} {order.customer?.lastName}
                        </p>
                        <div className="mt-0.5 flex items-center gap-3 text-xs text-gray-500">
                          {order.customer?.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {order.customer.email}
                            </span>
                          )}
                          {order.customer?.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {order.customer.phone}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {order.trip?.title || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                        {order.tripDate?.startDate ? (
                          <span className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(order.tripDate.startDate)}
                          </span>
                        ) : order.requestedDate ? (
                          <span className="flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {formatDate(order.requestedDate)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                        {formatPrice(order.totalPrice || 0)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`badge ${cfg.className}`}>{cfg.label}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/orders/${order.id}`} className="btn-ghost btn-sm">
                          <Eye className="h-4 w-4" />
                          Details
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </FacetedListLayout>
    </div>
  );
}
